import { appVersion, fullAppVersion, refreshApplication } from "../lib/appRuntime";
import {
  Building2,
  ChevronRight,
  Download,
  Home,
  RefreshCw,
  UserRound,
  Users,
  Wifi,
  WifiOff,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

export const PullToRefresh = () => {
  const startY = useRef(null);
  const distanceRef = useRef(0);
  const [distance, setDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const threshold = 90;
  useEffect(() => {
    const start = (event) => {
      if (globalThis.scrollY > 0 || event.touches.length !== 1 || refreshing)
        return;
      startY.current = event.touches[0].clientY;
      distanceRef.current = 0;
    };
    const move = (event) => {
      if (startY.current == null || globalThis.scrollY > 0) return;
      const pull = Math.max(0, event.touches[0].clientY - startY.current);
      const next = Math.min(120, pull * 0.55);
      if (next > 4) event.preventDefault();
      distanceRef.current = next;
      setDistance(next);
    };
    const end = () => {
      startY.current = null;
      if (distanceRef.current >= threshold) {
        setRefreshing(true);
        setDistance(threshold);
        refreshApplication();
      } else {
        distanceRef.current = 0;
        setDistance(0);
      }
    };
    addEventListener("touchstart", start, { passive: true });
    addEventListener("touchmove", move, { passive: false });
    addEventListener("touchend", end, { passive: true });
    addEventListener("touchcancel", end, { passive: true });
    return () => {
      removeEventListener("touchstart", start);
      removeEventListener("touchmove", move);
      removeEventListener("touchend", end);
      removeEventListener("touchcancel", end);
    };
  }, [refreshing]);
  const ready = distance >= threshold;
  return (
    <div
      className={`pull-to-refresh ${distance > 0 || refreshing ? "visible" : ""} ${ready ? "ready" : ""} ${refreshing ? "refreshing" : ""}`}
      style={{ transform: `translate(-50%, ${distance - 54}px)` }}
      role="status"
      aria-live="polite"
    >
      <RefreshCw />
      <span>
        {refreshing
          ? "Refreshing…"
          : ready
            ? "Release to refresh"
            : "Pull to refresh"}
      </span>
    </div>
  );
};

export const Brand = ({ compact = false }) => (
  <div className={compact ? "compact-brand" : "claim-brand"}>
    <span>◎</span>
    <b>OCHE</b>
    {!compact && <em>LEAGUE MANAGER</em>}
  </div>
);

export const LeagueIdentityHeader = ({
  organizationName,
  leagueName,
  online,
  offlineLabel = "Offline ready",
  onSwitchOrganization,
  canSwitchOrganization = false,
}) => {
  return (
    <header className="league-app-header">
      <button
        type="button"
        className={`league-identity ${canSwitchOrganization ? "switchable" : ""}`}
        onClick={canSwitchOrganization ? onSwitchOrganization : undefined}
        aria-label={canSwitchOrganization ? `Switch from ${organizationName}` : undefined}
      >
        <span><Building2 size={18} /> CURRENT ORGANIZATION{canSwitchOrganization && <ChevronRight size={17} />}</span>
        <strong>{organizationName || "Your Organization"}</strong>
        <small>{leagueName || "Your League"}</small>
      </button>
      <div className="connection-status">
        <div className={`connection-pill ${online ? "online" : "offline"}`}>
          {online ? <Wifi size={13} /> : <WifiOff size={13} />}
          {online ? "Synced" : offlineLabel}
        </div>
        <small className="app-version" title={`Version ${fullAppVersion}`}>
          Version {appVersion}
        </small>
      </div>
    </header>
  );
};

export const EmptyState = ({ icon, title, text }) => (
  <section className="empty-state">
    <div>{icon}</div>
    <h2>{title}</h2>
    <p>{text}</p>
  </section>
);

export const NavButton = ({ active, prominent, onClick, icon, label }) => (
  <button
    className={`${active ? "active" : ""} ${prominent ? "prominent" : ""}`}
    onClick={onClick}
  >
    {icon}
    <span>{label}</span>
  </button>
);

export const BrowserInstallRecommendation = ({ install }) => {
  const [dismissed, setDismissed] = useState(
    () =>
      sessionStorage.getItem("oche-install-recommendation-dismissed") ===
      "true",
  );
  if (install.installed || dismissed) return null;
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const dismiss = () => {
    sessionStorage.setItem("oche-install-recommendation-dismissed", "true");
    setDismissed(true);
  };
  return (
    <aside
      className="install-recommendation"
      aria-label="Install Oche League Manager"
    >
      <Download />
      <div>
        <b>Oche works best installed</b>
        <span>Faster match-night access, offline support, and app alerts.</span>
        {!install.available && (
          <small>
            {ios
              ? "Tap Share, then Add to Home Screen."
              : "Open your browser menu and choose Install app."}
          </small>
        )}
      </div>
      {install.available && (
        <button className="install-now" onClick={install.install}>
          Install
        </button>
      )}
      <button
        className="install-dismiss"
        aria-label="Dismiss installation recommendation"
        onClick={dismiss}
      >
        ×
      </button>
    </aside>
  );
};

export const PerspectiveSwitch = ({ perspective, captainTeams, onChange }) => {
  const pending = captainTeams.reduce(
    (sum, team) => sum + (team.pendingRegistrationCount || 0),
    0,
  );
  return (
    <div
      className="perspective-switch"
      role="group"
      aria-label="Choose dashboard perspective"
    >
      <button
        className={perspective === "player" ? "active" : ""}
        onClick={() => onChange("player")}
      >
        <UserRound /> Player
      </button>
      <button
        className={perspective === "team" ? "active" : ""}
        onClick={() => onChange("team")}
      >
        <Users /> Team{pending > 0 && <span>{pending}</span>}
      </button>
    </div>
  );
};
